import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TemplatesSection({ user, platforms }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customizing, setCustomizing] = useState(false);
  const [customizedContent, setCustomizedContent] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');

  // Use current_industry for admin switcher, fallback to industry
  const effectiveIndustry = user.current_industry || user.industry;

  const templates = {
    'Product Launch': {
      icon: '🚀',
      description: 'Announce new product or service',
      template: '🚀 Exciting News! We\'re thrilled to introduce [Product Name]! \n\n✨ [Key Feature 1]\n✨ [Key Feature 2]\n✨ [Key Feature 3]\n\nAvailable now at [Link]\n\n#ProductLaunch #Innovation #{Industry}'
    },
    'Tips & Tricks': {
      icon: '💡',
      description: 'Share valuable industry tips',
      template: '💡 Pro Tip for {Industry}:\n\n[Tip Description]\n\n👉 Why it matters:\n[Benefit Explanation]\n\nTry it and let us know how it works for you!\n\n#Tips #ProAdvice #{Industry}'
    },
    'Behind the Scenes': {
      icon: '🎬',
      description: 'Show your work process',
      template: '🎬 Behind the Scenes at [Company Name]!\n\nHere\'s how we [Process/Activity]...\n\n[Description of what\'s happening]\n\nWe love what we do! ❤️\n\n#BehindTheScenes #TeamWork #{Industry}'
    },
    'Customer Success': {
      icon: '⭐',
      description: 'Share customer testimonials',
      template: '⭐ Customer Success Story!\n\n"[Customer Quote]"\n\n- [Customer Name], [Customer Company]\n\nWe\'re proud to help our clients succeed! 🎉\n\n#CustomerSuccess #Testimonial #{Industry}'
    },
    'Industry News': {
      icon: '📰',
      description: 'Share trending industry updates',
      template: '📰 Industry Update:\n\n[News Headline]\n\n[Brief Summary]\n\n💭 Our take: [Your Opinion]\n\nWhat do you think? Share your thoughts below!\n\n#News #{Industry} #Trending'
    },
    'Question & Engagement': {
      icon: '❓',
      description: 'Ask questions to boost engagement',
      template: '❓ Question for our community:\n\n[Your Question]\n\nA) [Option 1]\nB) [Option 2]\nC) [Option 3]\n\nDrop your answer in the comments! 👇\n\n#Community #Question #{Industry}'
    },
    'Event Promotion': {
      icon: '📅',
      description: 'Promote events or webinars',
      template: '📅 Save the Date!\n\nJoin us for [Event Name]\n\n📍 [Location/Platform]\n🕐 [Date & Time]\n\n[Brief Event Description]\n\nRegister now: [Link]\n\n#Event #Webinar #{Industry}'
    },
    'Motivational': {
      icon: '💪',
      description: 'Inspire your audience',
      template: '💪 Monday Motivation:\n\n"[Motivational Quote]"\n\n[Your thoughts on the quote]\n\nWhat keeps you motivated this week?\n\n#Motivation #Inspiration #{Industry}'
    }
  };

  const useTemplate = async (templateName) => {
    setSelectedTemplate(templateName);
    let content = templates[templateName].template;
    
    // Replace placeholders with industry
    content = content.replace(/{Industry}/g, effectiveIndustry);
    content = content.replace(/#{Industry}/g, `#${effectiveIndustry.replace(/\s+/g, '')}`);
    
    setCustomizedContent(content);
    setCustomizing(true);
  };

  const generateWithAI = async () => {
    if (!selectedPlatform) {
      alert('Please select a platform first');
      return;
    }

    try {
      const response = await axios.post(`${API}/generate/content`, {
        user_id: user.id,
        industry: effectiveIndustry,
        platform: selectedPlatform,
        prompt: `Create a ${selectedTemplate} post based on this template: ${customizedContent}`
      });
      
      setCustomizedContent(response.data.content);
    } catch (error) {
      alert('Failed to enhance with AI');
    }
  };

  const savePost = async () => {
    if (!selectedPlatform) {
      alert('Please select a platform first');
      return;
    }

    try {
      const postResponse = await axios.post(`${API}/posts`, {
        user_id: user.id,
        platform: selectedPlatform
      });

      await axios.put(`${API}/posts/${postResponse.data.id}`, {
        content: customizedContent
      });

      alert('Template saved as post!');
      setCustomizing(false);
      setSelectedTemplate(null);
    } catch (error) {
      alert('Failed to save post');
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold gradient-text mb-2">Content Templates</h2>
        <p className="style={{ color: 'var(--text-muted)' }}">Pre-made templates to jumpstart your content creation</p>
      </div>

      {!customizing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(templates).map(([name, template]) => (
            <div
              key={name}
              className="theme-card border rounded-xl p-5 hover:border-orange-500/50 transition card-hover cursor-pointer"
              onClick={() => useTemplate(name)}
            >
              <div className="text-4xl mb-3">{template.icon}</div>
              <h3 className="text-lg font-bold accent-text mb-2">{name}</h3>
              <p className="text-sm style={{ color: 'var(--text-muted)' }} mb-4">{template.description}</p>
              <button className="w-full px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition">
                Use Template
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="theme-card border rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Customize: {selectedTemplate}</h3>
            <button
              onClick={() => {
                setCustomizing(false);
                setSelectedTemplate(null);
              }}
              className="style={{ color: 'var(--text-muted)' }} hover:text-white"
            >
              ✕ Close
            </button>
          </div>

          {/* Platform Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Platform</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {platforms.map((platform) => (
                <button
                  key={platform}
                  onClick={() => setSelectedPlatform(platform)}
                  className={`p-3 rounded-lg border-2 transition ${
                    selectedPlatform === platform
                      ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                      : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-orange-500/50'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Editable Template */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Edit Template</label>
            <textarea
              className="w-full px-4 py-3 theme-card border text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
              rows="10"
              value={customizedContent}
              onChange={(e) => setCustomizedContent(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 Tip: Replace placeholders like [Product Name] with your actual content
            </p>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={generateWithAI}
              disabled={!selectedPlatform}
              className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition disabled:opacity-50"
            >
              ✨ Enhance with AI
            </button>
            <button
              onClick={savePost}
              disabled={!selectedPlatform}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition disabled:opacity-50"
            >
              💾 Save as Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplatesSection;
